import endpoints from '@app/src/api/endpoints'
import { useDeleteResource } from '@app/src/api/updateHooks'
import {
  TabularImportQueryKey,
  useUnfinishedTabularImportSessions,
} from '@app/src/pages/TabularImport/shared/tabularImportHooks'
import { useTabularImportEntity } from '@app/src/pages/TabularImport/shared/TabularImportEntityContext'
import type { ImportStatus, UnfinishedImportSession } from '@app/src/types/tabularImport'
import { getFileFormatLabel } from '@app/src/types/tabularImport'
import { Delete, Description, Schedule, TableChart } from '@mui/icons-material'
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { useConfirm } from 'material-ui-confirm'
import React from 'react'

type UnfinishedImportsListProps = {
  onSelectSession: (sessionId: string) => void
}

const statusDisplay: Record<string, { label: string; color: 'default' | 'primary' | 'warning' | 'info' }> = {
  Analyzing: { label: 'Analyzing', color: 'info' },
  AwaitingMappingApproval: { label: 'Awaiting Mapping Review', color: 'warning' },
  MappingApproved: { label: 'Mapping Approved', color: 'primary' },
  Processing: { label: 'Processing', color: 'info' },
  AwaitingReview: { label: 'Ready for Review', color: 'primary' },
  DetectingDuplicates: { label: 'Detecting Duplicates', color: 'info' },
  DeduplicationReview: { label: 'Deduplication Review', color: 'primary' },
  Importing: { label: 'Importing', color: 'info' },
}

function getStatusLabel(status: ImportStatus): string {
  return statusDisplay[status]?.label ?? status
}

function getStatusColor(status: ImportStatus): 'default' | 'primary' | 'warning' | 'info' {
  return statusDisplay[status]?.color ?? 'default'
}

const UnfinishedImportCard: React.FC<{
  session: UnfinishedImportSession
  onSelect: () => void
  onDelete: () => void
  isDeleting: boolean
}> = ({ session, onSelect, onDelete, isDeleting }) => {
  const timeAgo = formatDistanceToNow(new Date(session.createdAt), { addSuffix: true })
  const fileIcon = session.fileFormat === 'Xlsx' ? <TableChart color="action" /> : <Description color="action" />

  return (
    <Card variant="outlined">
      <CardActionArea onClick={onSelect}>
        <CardContent
          sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Stack display="flex" flexDirection="row" alignItems="center" gap={2}>
            {fileIcon}
            <Box>
              <Typography variant="subtitle1" fontWeight="medium">
                {session.fileName}
              </Typography>
              <Box display="flex" alignItems="center" gap={0.5} mt={0.5} flexWrap="wrap">
                <Schedule fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {timeAgo}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mx: 1 }}>
                  •
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {session.totalRows} rows
                </Typography>
              </Box>
            </Box>
          </Stack>
          <Stack display="flex" direction="row" alignItems="center" gap={2}>
            <Chip label={getFileFormatLabel(session.fileFormat)} size="small" variant="outlined" />
            <Chip label={getStatusLabel(session.status)} color={getStatusColor(session.status)} size="small" />
            <Tooltip title="Delete draft">
              <IconButton
                aria-label="Delete draft"
                size="small"
                disabled={isDeleting}
                onClick={e => {
                  e.stopPropagation()
                  onDelete()
                }}
              >
                {isDeleting ? <CircularProgress size={18} /> : <Delete fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

const UnfinishedImportsList: React.FC<UnfinishedImportsListProps> = ({ onSelectSession }) => {
  const { entityType } = useTabularImportEntity()
  const { data: sessions, isLoading, error } = useUnfinishedTabularImportSessions()
  const confirm = useConfirm()
  const queryClient = useQueryClient()
  const { mutate: deleteResource, isPending: isDeleting, variables } = useDeleteResource<void>()
  const deletingUrl = isDeleting ? variables?.url : null

  const handleDelete = (session: UnfinishedImportSession): void => {
    confirm({
      title: 'Delete draft?',
      description: `This permanently deletes the import draft for "${session.fileName}". This cannot be undone.`,
      confirmationText: 'Delete',
      confirmationButtonProps: { variant: 'contained', color: 'error' },
    })
      .then(() =>
        deleteResource(
          { url: endpoints.tabularImportSession(entityType, session.sessionId) },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: TabularImportQueryKey.unfinishedSessions(entityType) })
            },
          },
        ),
      )
      .catch(() => undefined)
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load unfinished imports
      </Alert>
    )
  }

  if (!sessions || sessions.length === 0) {
    return null
  }

  return (
    <Box mb={4}>
      <Typography variant="h6" gutterBottom>
        Unfinished imports
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Continue where you left off or start a new import below.
      </Typography>
      <Stack spacing={2}>
        {sessions.map(session => (
          <UnfinishedImportCard
            key={session.sessionId}
            session={session}
            onSelect={() => onSelectSession(session.sessionId)}
            onDelete={() => handleDelete(session)}
            isDeleting={deletingUrl === endpoints.tabularImportSession(entityType, session.sessionId)}
          />
        ))}
      </Stack>
    </Box>
  )
}

export default UnfinishedImportsList
